import { inject, Injectable } from '@angular/core';
import { Observable, of, switchMap, map } from 'rxjs';
import { isEmpty } from 'lodash';

// Types
import { ApiToCallOffline, ToFreeze } from '../../../shared/types/common';
import { TrackingActionsParams, UseTrialModeParams } from '../../../shared/types/params';
import { StorageKey } from '../../../shared/types/storage';

// Services
import { CommonService } from '../common/common.service';
import { LoggerService } from '../logger/logger.service';
import { StorageService } from '../storage/storage.service';
import { ActivationCodeService } from '../activation-code/activation-code.service';
import { TrialService } from '../trial/trial.service';

@Injectable({ providedIn: 'root' })
export class OfflineService {
  public isAlreadyGettingOfflineFile: boolean = false;

  private readonly common = inject(CommonService);
  private readonly logger = inject(LoggerService);
  private readonly storage = inject(StorageService);
  private readonly activationCode = inject(ActivationCodeService);
  private readonly trial = inject(TrialService);

  addApiToCallWhenOnline(
    apiToCall: ApiToCallOffline['apiToCall'],
    params?: UseTrialModeParams | TrackingActionsParams,
  ): void {
    this.logger.consoleLog('OfflineService', 'addApiToCallWhenOnline');

    this.storage
      .getFromStorage<Array<ApiToCallOffline>>(StorageKey.ApisToCallOffline)
      .pipe(
        switchMap((apisToCallOffline) => {
          this.logger.consoleLog('OfflineService', 'addApiToCallWhenOnline', { apisToCallOffline });
          if (!apisToCallOffline) {
            return this.storage
              .setOnStorage(StorageKey.ApisToCallOffline, [] as Array<ApiToCallOffline>)
              .pipe(map(() => []));
          } else {
            return of(apisToCallOffline);
          }
        }),
      )
      .subscribe({
        next: (apisToCallOffline: Array<ApiToCallOffline>) => {
          this.logger.consoleLog('OfflineService', 'addApiToCallWhenOnline', { apisToCallOffline });

          const apiToUpdate = apisToCallOffline.findIndex((api) => api.apiToCall === apiToCall);

          if (apiToUpdate !== -1) {
            if (apisToCallOffline[apiToUpdate].params === undefined) {
              apisToCallOffline[apiToUpdate].params = [];
            }

            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            (apisToCallOffline[apiToUpdate].params as Array<UseTrialModeParams | TrackingActionsParams>).push(params);
          } else {
            apisToCallOffline.push({ apiToCall, params: [params] });
          }

          this.storage.setOnStorage(StorageKey.ApisToCallOffline, apisToCallOffline).subscribe();
        },
      });
  }

  getApisToCallWhenOnline(): Observable<Array<ApiToCallOffline>> {
    return this.storage.getFromStorage<Array<ApiToCallOffline>>(StorageKey.ApisToCallOffline).pipe(
      map((apisToCallOffline) => {
        if (apisToCallOffline && apisToCallOffline.length) {
          return apisToCallOffline;
        } else {
          return [];
        }
      }),
    );
  }

  removeApisToCallWhenOnline(): Observable<void> {
    return this.storage.removeFromStorage(StorageKey.ApisToCallOffline);
  }

  appOpenedOffline(): void {
    this.logger.consoleLog('OfflineService', 'appOpenedOffline', {
      configFile: this.common.configFile,
    });

    this.common.getUniqueDeviceID();
    this.common.getConfigFile().subscribe();

    this.storage.getFreezedState('OfflineService 168').subscribe({
      next: (lastState: ToFreeze) => {
        if (lastState !== null && !isEmpty(lastState)) {
          this.trial.hasRequestedTrialMode = lastState.hasRequestedTrialMode;
          this.trial.shouldEnableRequestTrial = lastState.shouldEnableRequestTrial;
          this.trial.trialsAvailable = lastState.trialsAvailable;
          this.trial.timeTrialExpiration = lastState.timeTrialExpiration;
          this.trial.isTrialMode = lastState.isTrialMode;
          this.trial.hasTrialExpired = lastState.hasTrialExpired;
          this.trial.hasTimeDuration = lastState.hasTimeDuration;

          this.activationCode.activationCodeValidity = { ...lastState.activationCodeValidity };
        }
      },
    });
  }

  freezeLastState(): void {
    this.logger.consoleLog('OfflineService', StorageKey.FreezeLastState);

    let toFreeze = {
      hasRequestedTrialMode: this.trial.hasRequestedTrialMode,
      shouldEnableRequestTrial: this.trial.shouldEnableRequestTrial,
      trialsAvailable: this.trial.trialsAvailable,
      timeTrialExpiration: this.trial.timeTrialExpiration,
      isTrialMode: this.trial.isTrialMode,
      hasTrialExpired: this.trial.hasTrialExpired,
      hasTimeDuration: this.trial.hasTimeDuration,
    } as ToFreeze;

    if (this.activationCode.activationCodeValidity) {
      toFreeze = { ...toFreeze, activationCodeValidity: this.activationCode.activationCodeValidity };
    }

    this.storage.setOnStorage(StorageKey.FreezeLastState, toFreeze).subscribe();
  }
}
